import {Group, createStyles} from '@mantine/core';
import React, {RefObject} from 'react';

import {PlateMapType} from 'samples/plate-maps/annotate-fill-plate-modal/AnnotateFillPlateModalConstants';
import {GenericPlateMapDisplayParams, PlateMapWellDisplayLayer} from 'samples/plate-maps/PlateMapConstants';
import PlateMapPane from 'samples/plate-maps/PlateMapPane';
import {PlateMapWellSelectionSection} from 'samples/plate-maps/PlateMapWellSelectionSection';
import PreviewWellRolesButton from 'samples/plate-maps/PreviewWellRolesButton';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';

export interface PlateMapPreviewSectionProps {
  plateMapDisplayParams: GenericPlateMapDisplayParams;
  displayLayer: PlateMapWellDisplayLayer;
  selectedWellIndices: Set<number>;
  setSelectedWellIndices: (indices: Set<number>) => void;
  previewedWellIndices: Set<number>;
  totalWells: number;
  previewWellRoles: boolean;
  onTogglePreviewWellRoles: () => void;
  shouldDisableWellRolePreview: boolean;
  onClickClear: () => void;
  previewSectionRef: RefObject<HTMLDivElement>;
  previewHeaderRef: RefObject<HTMLDivElement>;
  previewHeaderTextRef: RefObject<HTMLParagraphElement>;
  modalRef?: RefObject<HTMLDivElement>;
  maxPlateViewHeight: string;
  showFinalMeasurements: boolean;
  plateMapType: PlateMapType;
  isViewOnly?: boolean;
  /** Content to render above the header (e.g. validation banners). */
  aboveHeader?: JSX.Element | null;
  /** Replaces the well-selection section when in AI preview mode. */
  headerLeadingSlot?: JSX.Element;
}

/**
 * Plate preview area shared between AnnotatePlateStep and DefineScratchPlateMapStep.
 * Renders the well-selection header, optional preview-well-roles toggle, clear button, and PlateMapPane.
 */
export default function PlateMapPreviewSection({
  plateMapDisplayParams,
  displayLayer,
  selectedWellIndices,
  setSelectedWellIndices,
  previewedWellIndices,
  totalWells,
  previewWellRoles,
  onTogglePreviewWellRoles,
  shouldDisableWellRolePreview,
  onClickClear,
  previewSectionRef,
  previewHeaderRef,
  previewHeaderTextRef,
  modalRef,
  maxPlateViewHeight,
  showFinalMeasurements,
  plateMapType,
  isViewOnly,
  aboveHeader,
  headerLeadingSlot,
}: PlateMapPreviewSectionProps): JSX.Element {
  const {classes} = useStyles();

  const header = (
    <div ref={previewHeaderRef}>
      <Group position="apart">
        {headerLeadingSlot != null ? (
          headerLeadingSlot
        ) : !isViewOnly ? (
          <PlateMapWellSelectionSection
            selectedWellIndices={selectedWellIndices}
            paragraphRef={previewHeaderTextRef}
            setSelectedWellIndices={setSelectedWellIndices}
            totalWells={totalWells}
          />
        ) : (
          <div />
        )}
        <Group spacing="xs">
          {displayLayer === PlateMapWellDisplayLayer.CONCENTRATION && (
            <PreviewWellRolesButton
              previewWellRoles={previewWellRoles}
              onToggle={onTogglePreviewWellRoles}
              disabled={shouldDisableWellRolePreview}
            />
          )}
          <Button
            icon="close"
            size={ButtonSize.SMALL}
            buttonStyle={ButtonStyle.TERTIARY_GRAY}
            text={selectedWellIndices.size === 0 ? 'Clear all wells' : 'Clear wells'}
            onClick={onClickClear}
            disabled={isViewOnly}
          />
        </Group>
      </Group>
    </div>
  );

  return (
    <div className={classes.previewSection} ref={previewSectionRef}>
      {aboveHeader}
      <PlateMapPane
        header={header}
        plateMapDisplayParams={plateMapDisplayParams}
        isPlateMapViewOnly={isViewOnly ?? false}
        selectedWellIndices={selectedWellIndices}
        setSelectedWellIndices={setSelectedWellIndices}
        previewedWellIndices={previewedWellIndices}
        deselectWellsOnClickRefs={[previewSectionRef, previewHeaderRef, previewHeaderTextRef]}
        modalRef={modalRef}
        maxPlateViewHeight={maxPlateViewHeight}
        showFinalMeasurements={showFinalMeasurements}
        plateMapType={plateMapType}
      />
    </div>
  );
}

const useStyles = createStyles(() => ({
  previewSection: {
    flex: '1 1 auto',
    overflow: 'auto',
  },
}));
